import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface FirstConsultationChatbotProps {
  token: string;
  onSuccess: () => void;
}

interface QuestionnaireData {
  // Dados pessoais
  name: string;
  sex: string;
  birthDate: string;
  maritalStatus: string;
  phone: string;
  healthPlan: string;
  education: string;
  profession: string;
  birthplace: string;
  residence: string;
  cpf: string;
  
  // Motivo da consulta
  mainReason: string;
  
  // Doenças e medicamentos
  diseases: string;
  medications: string;
  allergies: string;
  
  // Estilo de vida
  physicalActivity: string;
  smoking: {
    isSmoker: boolean;
    cigarettesPerDay: string;
    startedAge: string;
    quitDate: string;
  };
  alcohol: {
    consumes: boolean;
    drinkType: string;
    quantity: string;
    frequency: string;
    startedAge: string;
    quitDate: string;
  };
  
  // Histórico ginecológico (se feminino)
  gynecological: {
    pregnancies: string;
    vaginalBirths: string;
    cesareans: string;
    miscarriages: string;
    menopauseAge: string;
    hormoneReplacement: boolean;
    hormoneReplacementYears: string;
  };
  
  // Histórico familiar
  familyHistory: string;
  
  // Cirurgias e hospitalizações
  surgeries: string;
  
  // Vacinas
  vaccines: {
    flu: { received: boolean; year: string };
    covid19: { received: boolean; year: string };
    rsv: { received: boolean; year: string };
    yellowFever: { received: boolean; year: string };
    hepatitisB: { received: boolean; year: string };
    pneumococcal: { received: boolean; type: string; year: string };
    dtpa: { received: boolean; year: string };
    shingles: { received: boolean; year: string };
    other: { received: boolean; details: string };
  };
  
  // Outras informações
  otherInfo: string;
}

type Step = 
  | 'personal_data'
  | 'main_reason'
  | 'diseases_medications'
  | 'allergies'
  | 'physical_activity'
  | 'smoking'
  | 'alcohol'
  | 'gynecological'
  | 'family_history'
  | 'surgeries'
  | 'vaccines'
  | 'other_info'
  | 'review';

export function FirstConsultationChatbot({ token, onSuccess }: FirstConsultationChatbotProps) {
  const [currentStep, setCurrentStep] = useState<Step>('personal_data');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<QuestionnaireData>({
    name: '',
    sex: '',
    birthDate: '',
    maritalStatus: '',
    phone: '',
    healthPlan: '',
    education: '',
    profession: '',
    birthplace: '',
    residence: '',
    cpf: '',
    mainReason: '',
    diseases: '',
    medications: '',
    allergies: '',
    physicalActivity: '',
    smoking: {
      isSmoker: false,
      cigarettesPerDay: '',
      startedAge: '',
      quitDate: '',
    },
    alcohol: {
      consumes: false,
      drinkType: '',
      quantity: '',
      frequency: '',
      startedAge: '',
      quitDate: '',
    },
    gynecological: {
      pregnancies: '',
      vaginalBirths: '',
      cesareans: '',
      miscarriages: '',
      menopauseAge: '',
      hormoneReplacement: false,
      hormoneReplacementYears: '',
    },
    familyHistory: '',
    surgeries: '',
    vaccines: {
      flu: { received: false, year: '' },
      covid19: { received: false, year: '' },
      rsv: { received: false, year: '' },
      yellowFever: { received: false, year: '' },
      hepatitisB: { received: false, year: '' },
      pneumococcal: { received: false, type: '', year: '' },
      dtpa: { received: false, year: '' },
      shingles: { received: false, year: '' },
      other: { received: false, details: '' },
    },
    otherInfo: '',
  });

  const steps: Step[] = [
    'personal_data',
    'main_reason',
    'diseases_medications',
    'allergies',
    'physical_activity',
    'smoking',
    'alcohol',
    'gynecological',
    'family_history',
    'surgeries',
    'vaccines',
    'other_info',
    'review',
  ];

  const currentStepIndex = steps.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Pular etapa ginecológica se sexo não for feminino
  useEffect(() => {
    if (currentStep === 'gynecological' && data.sex !== 'F') {
      const gynecologicalIndex = steps.indexOf('gynecological');
      if (currentStepIndex === gynecologicalIndex && gynecologicalIndex < steps.length - 1) {
        setCurrentStep(steps[gynecologicalIndex + 1]);
      }
    }
  }, [currentStep, data.sex, currentStepIndex]);

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStep(steps[currentStepIndex + 1]);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStep(steps[currentStepIndex - 1]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('submission_type', 'text');
      formData.append('text_content', 'Questionário de Primeiro Atendimento');
      formData.append('metadata', JSON.stringify({
        type: 'first_consultation',
        responses: data,
      }));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-assisted-collection`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const result = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success('Questionário enviado com sucesso!');
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar questionário');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'personal_data':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Dados Pessoais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                  placeholder="Seu nome completo"
                />
              </div>
              <div>
                <Label>Sexo *</Label>
                <Select value={data.sex} onValueChange={(value) => setData({ ...data, sex: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Masculino</SelectItem>
                    <SelectItem value="F">Feminino</SelectItem>
                    <SelectItem value="O">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data de Nascimento *</Label>
                <Input
                  type="date"
                  value={data.birthDate}
                  onChange={(e) => setData({ ...data, birthDate: e.target.value })}
                />
              </div>
              <div>
                <Label>Estado Civil</Label>
                <Select value={data.maritalStatus} onValueChange={(value) => setData({ ...data, maritalStatus: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solteiro">Solteiro(a)</SelectItem>
                    <SelectItem value="casado">Casado(a)</SelectItem>
                    <SelectItem value="divorciado">Divorciado(a)</SelectItem>
                    <SelectItem value="viuvo">Viúvo(a)</SelectItem>
                    <SelectItem value="uniao">União Estável</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Telefone *</Label>
                <Input
                  value={data.phone}
                  onChange={(e) => setData({ ...data, phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div>
                <Label>Plano de Saúde</Label>
                <Input
                  value={data.healthPlan}
                  onChange={(e) => setData({ ...data, healthPlan: e.target.value })}
                  placeholder="Nome do plano"
                />
              </div>
              <div>
                <Label>Escolaridade</Label>
                <Input
                  value={data.education}
                  onChange={(e) => setData({ ...data, education: e.target.value })}
                  placeholder="Anos de estudo ou nível"
                />
              </div>
              <div>
                <Label>Profissão</Label>
                <Input
                  value={data.profession}
                  onChange={(e) => setData({ ...data, profession: e.target.value })}
                />
              </div>
              <div>
                <Label>Naturalidade</Label>
                <Input
                  value={data.birthplace}
                  onChange={(e) => setData({ ...data, birthplace: e.target.value })}
                  placeholder="Cidade/Estado"
                />
              </div>
              <div>
                <Label>Residência</Label>
                <Input
                  value={data.residence}
                  onChange={(e) => setData({ ...data, residence: e.target.value })}
                  placeholder="Cidade/Estado"
                />
              </div>
              <div>
                <Label>CPF</Label>
                <Input
                  value={data.cpf}
                  onChange={(e) => setData({ ...data, cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          </div>
        );

      case 'main_reason':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Motivo Principal da Consulta</h3>
            <Textarea
              value={data.mainReason}
              onChange={(e) => setData({ ...data, mainReason: e.target.value })}
              placeholder="Descreva o motivo principal da sua consulta..."
              rows={6}
            />
          </div>
        );

      case 'diseases_medications':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Doenças e Medicamentos</h3>
            <div>
              <Label>Você possui alguma doença? Se sim, quais você lembra?</Label>
              <Textarea
                value={data.diseases}
                onChange={(e) => setData({ ...data, diseases: e.target.value })}
                placeholder="Liste as doenças que você possui ou já teve..."
                rows={4}
              />
            </div>
            <div>
              <Label>Medicamentos em uso (incluindo vitaminas e fitoterápicos)</Label>
              <Textarea
                value={data.medications}
                onChange={(e) => setData({ ...data, medications: e.target.value })}
                placeholder="Liste os medicamentos que você está usando..."
                rows={4}
              />
            </div>
          </div>
        );

      case 'allergies':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Alergias</h3>
            <Textarea
              value={data.allergies}
              onChange={(e) => setData({ ...data, allergies: e.target.value })}
              placeholder="Descreva suas alergias, se houver..."
              rows={4}
            />
          </div>
        );

      case 'physical_activity':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Atividade Física</h3>
            <RadioGroup
              value={data.physicalActivity}
              onValueChange={(value) => setData({ ...data, physicalActivity: value })}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="sedentary" id="sedentary" />
                <Label htmlFor="sedentary">Sedentário</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="regular" id="regular" />
                <Label htmlFor="regular">Regular</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="performance" id="performance" />
                <Label htmlFor="performance">Performance</Label>
              </div>
            </RadioGroup>
          </div>
        );

      case 'smoking':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Tabagismo</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="smoker"
                checked={data.smoking.isSmoker}
                onCheckedChange={(checked) =>
                  setData({
                    ...data,
                    smoking: { ...data.smoking, isSmoker: checked === true },
                  })
                }
              />
              <Label htmlFor="smoker">Fumante ou ex-fumante</Label>
            </div>
            {data.smoking.isSmoker && (
              <div className="space-y-4 pl-6 border-l-2">
                <div>
                  <Label>Quantos cigarros por dia?</Label>
                  <Input
                    value={data.smoking.cigarettesPerDay}
                    onChange={(e) =>
                      setData({
                        ...data,
                        smoking: { ...data.smoking, cigarettesPerDay: e.target.value },
                      })
                    }
                    placeholder="Ex: 10"
                  />
                </div>
                <div>
                  <Label>Qual idade iniciou?</Label>
                  <Input
                    value={data.smoking.startedAge}
                    onChange={(e) =>
                      setData({
                        ...data,
                        smoking: { ...data.smoking, startedAge: e.target.value },
                      })
                    }
                    placeholder="Ex: 18"
                  />
                </div>
                <div>
                  <Label>Se cessou, quando?</Label>
                  <Input
                    value={data.smoking.quitDate}
                    onChange={(e) =>
                      setData({
                        ...data,
                        smoking: { ...data.smoking, quitDate: e.target.value },
                      })
                    }
                    placeholder="Ex: 2020"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'alcohol':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Consumo de Álcool</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="alcohol"
                checked={data.alcohol.consumes}
                onCheckedChange={(checked) =>
                  setData({
                    ...data,
                    alcohol: { ...data.alcohol, consumes: checked === true },
                  })
                }
              />
              <Label htmlFor="alcohol">Consome álcool</Label>
            </div>
            {data.alcohol.consumes && (
              <div className="space-y-4 pl-6 border-l-2">
                <div>
                  <Label>Qual bebida e quantidade?</Label>
                  <Input
                    value={data.alcohol.drinkType}
                    onChange={(e) =>
                      setData({
                        ...data,
                        alcohol: { ...data.alcohol, drinkType: e.target.value },
                      })
                    }
                    placeholder="Ex: Cerveja, 2 latas"
                  />
                </div>
                <div>
                  <Label>Com que frequência?</Label>
                  <Input
                    value={data.alcohol.frequency}
                    onChange={(e) =>
                      setData({
                        ...data,
                        alcohol: { ...data.alcohol, frequency: e.target.value },
                      })
                    }
                    placeholder="Ex: Finais de semana"
                  />
                </div>
                <div>
                  <Label>Qual idade iniciou?</Label>
                  <Input
                    value={data.alcohol.startedAge}
                    onChange={(e) =>
                      setData({
                        ...data,
                        alcohol: { ...data.alcohol, startedAge: e.target.value },
                      })
                    }
                    placeholder="Ex: 18"
                  />
                </div>
                <div>
                  <Label>Se cessou, quando?</Label>
                  <Input
                    value={data.alcohol.quitDate}
                    onChange={(e) =>
                      setData({
                        ...data,
                        alcohol: { ...data.alcohol, quitDate: e.target.value },
                      })
                    }
                    placeholder="Ex: 2020"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'gynecological':
        if (data.sex !== 'F') {
          return (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Esta seção não se aplica. Pulando...
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Histórico Ginecológico</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Número de gestações</Label>
                <Input
                  type="number"
                  value={data.gynecological.pregnancies}
                  onChange={(e) =>
                    setData({
                      ...data,
                      gynecological: { ...data.gynecological, pregnancies: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Label>Número de partos vaginais</Label>
                <Input
                  type="number"
                  value={data.gynecological.vaginalBirths}
                  onChange={(e) =>
                    setData({
                      ...data,
                      gynecological: { ...data.gynecological, vaginalBirths: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Label>Número de cesáreas</Label>
                <Input
                  type="number"
                  value={data.gynecological.cesareans}
                  onChange={(e) =>
                    setData({
                      ...data,
                      gynecological: { ...data.gynecological, cesareans: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Label>Número de abortos espontâneos</Label>
                <Input
                  type="number"
                  value={data.gynecological.miscarriages}
                  onChange={(e) =>
                    setData({
                      ...data,
                      gynecological: { ...data.gynecological, miscarriages: e.target.value },
                    })
                  }
                />
              </div>
              <div>
                <Label>Idade da menopausa (última menstruação)</Label>
                <Input
                  value={data.gynecological.menopauseAge}
                  onChange={(e) =>
                    setData({
                      ...data,
                      gynecological: { ...data.gynecological, menopauseAge: e.target.value },
                    })
                  }
                  placeholder="Ex: 50"
                />
              </div>
              <div>
                <Label>Usa terapia de reposição hormonal?</Label>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="hormone"
                    checked={data.gynecological.hormoneReplacement}
                    onCheckedChange={(checked) =>
                      setData({
                        ...data,
                        gynecological: {
                          ...data.gynecological,
                          hormoneReplacement: checked === true,
                        },
                      })
                    }
                  />
                  <Label htmlFor="hormone">Sim</Label>
                </div>
                {data.gynecological.hormoneReplacement && (
                  <Input
                    className="mt-2"
                    value={data.gynecological.hormoneReplacementYears}
                    onChange={(e) =>
                      setData({
                        ...data,
                        gynecological: {
                          ...data.gynecological,
                          hormoneReplacementYears: e.target.value,
                        },
                      })
                    }
                    placeholder="Por quantos anos?"
                  />
                )}
              </div>
            </div>
          </div>
        );

      case 'family_history':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Histórico Familiar</h3>
            <Textarea
              value={data.familyHistory}
              onChange={(e) => setData({ ...data, familyHistory: e.target.value })}
              placeholder="Doenças relevantes em pai, mãe, irmãos, filhos (câncer, infarto, derrame, diabetes, pressão alta, etc.)"
              rows={6}
            />
          </div>
        );

      case 'surgeries':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Cirurgias e Hospitalizações Prévias</h3>
            <Textarea
              value={data.surgeries}
              onChange={(e) => setData({ ...data, surgeries: e.target.value })}
              placeholder="Descreva cirurgias e hospitalizações anteriores..."
              rows={6}
            />
          </div>
        );

      case 'vaccines':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Vacinas Realizadas</h3>
            <p className="text-sm text-muted-foreground">
              Assinale apenas a última que você lembra e o ano
            </p>
            <div className="space-y-3">
              {[
                { key: 'flu', label: 'Gripe (Influenza)' },
                { key: 'covid19', label: 'Covid-19' },
                { key: 'rsv', label: 'Vírus sincicial respiratório' },
                { key: 'yellowFever', label: 'Febre amarela' },
                { key: 'hepatitisB', label: 'Hepatite B' },
                { key: 'pneumococcal', label: 'Pneumocócica' },
                { key: 'dtpa', label: 'dT / dTpa (difteria + tétano + coqueluche)' },
                { key: 'shingles', label: 'Herpes Zoster (Shingrix)' },
                { key: 'other', label: 'Outra(s)' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-4">
                  <Checkbox
                    checked={data.vaccines[key as keyof typeof data.vaccines].received}
                    onCheckedChange={(checked) =>
                      setData({
                        ...data,
                        vaccines: {
                          ...data.vaccines,
                          [key]: {
                            ...data.vaccines[key as keyof typeof data.vaccines],
                            received: checked === true,
                          },
                        },
                      })
                    }
                  />
                  <Label className="flex-1">{label}</Label>
                  {data.vaccines[key as keyof typeof data.vaccines].received && (
                    <>
                      {key === 'pneumococcal' && (
                        <Input
                          className="w-32"
                          placeholder="Tipo"
                          value={data.vaccines.pneumococcal.type}
                          onChange={(e) =>
                            setData({
                              ...data,
                              vaccines: {
                                ...data.vaccines,
                                pneumococcal: {
                                  ...data.vaccines.pneumococcal,
                                  type: e.target.value,
                                },
                              },
                            })
                          }
                        />
                      )}
                      {key === 'other' ? (
                        <Input
                          className="flex-1"
                          placeholder="Detalhes"
                          value={data.vaccines.other.details}
                          onChange={(e) =>
                            setData({
                              ...data,
                              vaccines: {
                                ...data.vaccines,
                                other: {
                                  ...data.vaccines.other,
                                  details: e.target.value,
                                },
                              },
                            })
                          }
                        />
                      ) : (
                        <Input
                          className="w-24"
                          placeholder="Ano"
                          value={(data.vaccines[key as keyof typeof data.vaccines] as { received: boolean; year?: string }).year || ''}
                          onChange={(e) =>
                            setData({
                              ...data,
                              vaccines: {
                                ...data.vaccines,
                                [key]: {
                                  ...data.vaccines[key as keyof typeof data.vaccines],
                                  year: e.target.value,
                                },
                              },
                            })
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'other_info':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Outras Informações Relevantes</h3>
            <Textarea
              value={data.otherInfo}
              onChange={(e) => setData({ ...data, otherInfo: e.target.value })}
              placeholder="Qualquer outra informação que considere relevante..."
              rows={6}
            />
          </div>
        );

      case 'review':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Revisão</h3>
            <p className="text-sm text-muted-foreground">
              Revise suas respostas antes de enviar. Você pode voltar para editar qualquer informação.
            </p>
            <div className="space-y-2 text-sm">
              <div><strong>Nome:</strong> {data.name || 'Não informado'}</div>
              <div><strong>Sexo:</strong> {data.sex || 'Não informado'}</div>
              <div><strong>Data de Nascimento:</strong> {data.birthDate || 'Não informado'}</div>
              <div><strong>Motivo da Consulta:</strong> {data.mainReason || 'Não informado'}</div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Questionário de Primeiro Atendimento</CardTitle>
        <CardDescription>
          Preencha as informações abaixo. Você pode navegar entre as seções usando os botões.
        </CardDescription>
        <div className="w-full bg-muted rounded-full h-2 mt-4">
          <div
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Passo {currentStepIndex + 1} de {steps.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderStep()}

        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStepIndex === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Anterior
          </Button>
          {currentStep === 'review' ? (
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar Questionário'
              )}
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Próximo
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

